import { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import api from '../api/api';
import {
  Box,
  IconButton,
  CircularProgress,
  Typography,
  TextField,
  Avatar,
  Drawer,
  Chip,
} from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// ─── Markdown-like renderer for AI responses ───
function FormattedMessage({ text, isUser }) {
  if (isUser) return <span>{text}</span>;

  const lines = text.split('\n');
  const elements = [];
  let listItems = [];
  let listType = null; // 'ul' or 'ol'

  const flushList = () => {
    if (listItems.length > 0) {
      const Tag = listType === 'ol' ? 'ol' : 'ul';
      elements.push(
        <Tag key={`list-${elements.length}`} style={{
          margin: '6px 0', paddingLeft: 20,
          listStyleType: listType === 'ol' ? 'decimal' : 'disc',
        }}>
          {listItems.map((li, j) => (
            <li key={j} style={{ marginBottom: 3 }}>{renderInline(li)}</li>
          ))}
        </Tag>
      );
      listItems = [];
      listType = null;
    }
  };

  const renderInline = (str) => {
    // Bold: **text**
    const parts = str.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    if (!line) {
      flushList();
      elements.push(<div key={`br-${i}`} style={{ height: 6 }} />);
      continue;
    }

    // Heading: ### or ##
    if (line.startsWith('### ')) {
      flushList();
      elements.push(
        <div key={i} style={{ fontWeight: 700, fontSize: 13, color: '#701313', marginTop: 8, marginBottom: 2 }}>
          {renderInline(line.slice(4))}
        </div>
      );
      continue;
    }
    if (line.startsWith('## ')) {
      flushList();
      elements.push(
        <div key={i} style={{ fontWeight: 700, fontSize: 14, color: '#701313', marginTop: 8, marginBottom: 2 }}>
          {renderInline(line.slice(3))}
        </div>
      );
      continue;
    }

    // Horizontal rule
    if (line === '---' || line === '***') {
      flushList();
      elements.push(<hr key={i} style={{ border: 'none', borderTop: '1px solid #e5e5e5', margin: '8px 0' }} />);
      continue;
    }

    // Unordered list: - item or * item
    const ulMatch = line.match(/^[-*]\s+(.+)/);
    if (ulMatch) {
      if (listType !== 'ul') flushList();
      listType = 'ul';
      listItems.push(ulMatch[1]);
      continue;
    }

    // Ordered list: 1. item
    const olMatch = line.match(/^\d+\.\s+(.+)/);
    if (olMatch) {
      if (listType !== 'ol') flushList();
      listType = 'ol';
      listItems.push(olMatch[1]);
      continue;
    }

    // Regular paragraph
    flushList();
    elements.push(
      <div key={i} style={{ marginBottom: 2 }}>{renderInline(line)}</div>
    );
  }

  flushList();

  return <>{elements}</>;
}

const MIN_WIDTH = 380;
const MAX_WIDTH = 800;
const DEFAULT_WIDTH = 520;

const QUICK_ACTIONS = [
  'Total employees count',
  'Count of all form types',
  'Timesheet summary today',
  'Active jobs summary',
  'Total subcontractors',
  'HSE stats overview',
  'Pending drafts count',
  'Devices overview',
];

function AiSupportChat({ open, onClose }) {
  const userDetails = useSelector((state) => state.user.userDetails);
  const isAdmin = userDetails?.role === 1;

  const [activeTab, setActiveTab] = useState('chat');
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(DEFAULT_WIDTH);
  const [conversationId, setConversationId] = useState(null);
  const [viewingHistory, setViewingHistory] = useState(null);

  const messagesEndRef = useRef(null);
  const isResizing = useRef(false);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  // Fetch history when History tab is opened
  useEffect(() => {
    if (open && activeTab === 'history') {
      fetchHistory();
    }
  }, [open, activeTab]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get('/ai-support/history');
      setConversations(res?.conversations || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // --- Resize from left edge ---
  const onResizeStart = useCallback((e) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev) => {
      if (!isResizing.current) return;
      const newWidth = window.innerWidth - ev.clientX;
      setDrawerWidth(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth)));
    };

    const onMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  // --- Send message ---
  const handleSend = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;

    const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const userMsg = { role: 'user', text: trimmed, time: now };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const payload = { question: trimmed };
      if (conversationId) payload.conversation_id = conversationId;

      const res = await api.post('/ai-support/chat', payload);
      const answer = res?.answer || 'No response received.';
      const sources = res?.sources || [];

      // Save conversation_id for subsequent messages
      if (res?.conversation_id) {
        setConversationId(res.conversation_id);
      }

      let sourceText = '';
      if (sources.length > 0) {
        sourceText = '\n\nSources: ' + sources.map((s) => `${s.table} (${s.count})`).join(', ');
      }
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: answer + sourceText, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: 'Sorry, something went wrong. Please try again.', time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // --- New chat ---
  const handleNewChat = () => {
    setMessages([]);
    setConversationId(null);
    setViewingHistory(null);
    setActiveTab('chat');
  };

  // --- View a history conversation ---
  const handleViewHistory = async (convId) => {
    try {
      const res = await api.get(`/ai-support/history/${convId}`);
      const conv = res?.conversation;
      if (conv) {
        setMessages(conv.messages || []);
        setConversationId(conv.id);
        setViewingHistory(conv.id);
        setActiveTab('chat');
      }
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  };

  // --- Delete history item ---
  const handleDeleteHistory = async (convId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/ai-support/history/${convId}`);
      setConversations((prev) => prev.filter((c) => c.id !== convId));
      if (viewingHistory === convId) {
        setMessages([]);
        setConversationId(null);
        setViewingHistory(null);
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  };

  if (!isAdmin) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      sx={{
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          maxWidth: '90vw',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
          borderLeft: 'none',
          overflow: 'visible',
        },
      }}
    >
      {/* Resize handle */}
      <Box
        onMouseDown={onResizeStart}
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 5,
          cursor: 'ew-resize',
          zIndex: 10,
          '&:hover': { bgcolor: 'rgba(112,19,19,0.15)' },
        }}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>

        {/* Header with tabs */}
        <Box sx={{ borderBottom: '1px solid #e5e7eb', bgcolor: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5, pb: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 0.5, bgcolor: '#f3f4f6', borderRadius: 6, p: 0.4 }}>
              {['chat', 'history'].map((tab) => (
                <Box
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  sx={{
                    px: 2.5,
                    py: 0.7,
                    borderRadius: 5,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                    bgcolor: activeTab === tab ? '#fff' : 'transparent',
                    color: activeTab === tab ? '#111' : '#888',
                    boxShadow: activeTab === tab ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.2s',
                  }}
                >
                  {tab === 'history' ? `History` : 'Chat'}
                </Box>
              ))}
            </Box>
            <IconButton size="small" onClick={onClose} sx={{ color: '#666' }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>

        {/* ========== CHAT TAB ========== */}
        {activeTab === 'chat' && (
          <>
            {/* Messages area */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#fafafa',
              }}
            >
              {/* Back button when viewing old conversation */}
              {viewingHistory && (
                <Box sx={{ px: 2, pt: 1.5 }}>
                  <Chip
                    icon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                    label="New Chat"
                    size="small"
                    onClick={handleNewChat}
                    sx={{ cursor: 'pointer', fontWeight: 500 }}
                  />
                </Box>
              )}

              {/* Welcome screen when no messages */}
              {messages.length === 0 && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 3 }}>
                  <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#111', mb: 0.5 }}>
                    Hello {userDetails?.firstname || 'Admin'} 👋
                  </Typography>
                  <Typography sx={{ fontSize: 15, color: '#888', mb: 4 }}>
                    How can I help you today?
                  </Typography>
                </Box>
              )}

              {/* Messages */}
              {messages.length > 0 && (
                <Box sx={{ flex: 1, px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {messages.map((msg, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: 'flex',
                        flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                        gap: 1,
                        alignItems: 'flex-start',
                      }}
                    >
                      {msg.role === 'assistant' && (
                        <Avatar sx={{ width: 30, height: 30, bgcolor: '#701313', mt: 0.3 }}>
                          <SupportAgentIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                      )}
                      <Box sx={{ maxWidth: '80%' }}>
                        <Box
                          sx={{
                            px: 1.8,
                            py: 1.2,
                            borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                            bgcolor: msg.role === 'user' ? '#701313' : '#fff',
                            color: msg.role === 'user' ? '#fff' : '#333',
                            fontSize: 13.5,
                            lineHeight: 1.6,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                            wordBreak: 'break-word',
                            '& strong': { fontWeight: 700, color: msg.role === 'user' ? '#fff' : '#222' },
                            '& li': { fontSize: 13 },
                          }}
                        >
                          <FormattedMessage text={msg.text} isUser={msg.role === 'user'} />
                        </Box>
                        {msg.time && (
                          <Typography sx={{ fontSize: 10, color: '#aaa', mt: 0.4, textAlign: msg.role === 'user' ? 'right' : 'left', px: 0.5 }}>
                            {msg.time}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}

                  {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                      <Avatar sx={{ width: 30, height: 30, bgcolor: '#701313' }}>
                        <SupportAgentIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 0.6,
                        px: 1.8, py: 1.2, bgcolor: '#fff', borderRadius: 3,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                      }}>
                        {[0, 1, 2].map((i) => (
                          <Box
                            key={i}
                            sx={{
                              width: 8, height: 8, borderRadius: '50%',
                              bgcolor: '#701313',
                              animation: 'typingBounce 1.4s infinite ease-in-out',
                              animationDelay: `${i * 0.2}s`,
                              '@keyframes typingBounce': {
                                '0%, 80%, 100%': { transform: 'scale(0.4)', opacity: 0.4 },
                                '40%': { transform: 'scale(1)', opacity: 1 },
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  <div ref={messagesEndRef} />
                </Box>
              )}
            </Box>

            {/* Quick actions (only when no messages) */}
            {messages.length === 0 && (
              <Box sx={{ px: 2, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                {QUICK_ACTIONS.map((action) => (
                  <Chip
                    key={action}
                    label={action}
                    size="small"
                    onClick={() => handleSend(action)}
                    sx={{
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 500,
                      bgcolor: '#fff',
                      border: '1px solid #e0e0e0',
                      '&:hover': { bgcolor: '#f5f5f5', borderColor: '#701313', color: '#701313' },
                    }}
                  />
                ))}
              </Box>
            )}

            {/* Input area */}
            <Box sx={{ px: 2, pb: 1.5, pt: 1, borderTop: '1px solid #eee', bgcolor: '#fff' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  inputRef={inputRef}
                  fullWidth
                  size="small"
                  placeholder="Ask AI Assistant anything..."
                  value={input}
                  disabled={loading}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 6,
                      fontSize: 13.5,
                      bgcolor: '#f9f9f9',
                    },
                  }}
                />
                <IconButton
                  onClick={() => handleSend()}
                  disabled={loading || !input.trim()}
                  sx={{
                    background: loading || !input.trim() ? '#e0e0e0' : '#701313',
                    color: '#fff',
                    width: 38,
                    height: 38,
                    '&:hover': { background: loading || !input.trim() ? '#e0e0e0' : '#8a1a1a' },
                  }}
                >
                  {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Box>

              {/* New chat + disclaimer */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                {messages.length > 0 ? (
                  <Typography
                    onClick={handleNewChat}
                    sx={{ fontSize: 11, color: '#701313', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                  >
                    + New Chat
                  </Typography>
                ) : <span />}
                <Typography sx={{ fontSize: 10, color: '#bbb' }}>
                  AI Assistant can make mistakes. Double-check replies.
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* ========== HISTORY TAB ========== */}
        {activeTab === 'history' && (
          <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: '#fafafa' }}>
            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress size={28} sx={{ color: '#701313' }} />
              </Box>
            ) : conversations.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', px: 3 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#888', mb: 1 }}>No chat history yet</Typography>
                <Typography sx={{ fontSize: 13, color: '#aaa', textAlign: 'center' }}>
                  Your conversations will appear here automatically.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {conversations.map((conv) => (
                  <Box
                    key={conv.id}
                    onClick={() => handleViewHistory(conv.id)}
                    sx={{
                      p: 1.5,
                      bgcolor: '#fff',
                      borderRadius: 2,
                      cursor: 'pointer',
                      border: '1px solid #eee',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: '#701313', bgcolor: '#fef7f7' },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {conv.title}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#aaa', mt: 0.3 }}>
                        {conv.created_at?.split(' ')[0]} · {conv.message_count} messages
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleDeleteHistory(conv.id, e)}
                      sx={{ color: '#ccc', '&:hover': { color: '#e53935' } }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

export default AiSupportChat;
